import React, { useEffect, useState } from "react";
import MuiPaper from "Components/MuiPaper/MuiPaper";
import Translator from "Languages/Utility/Translator";
import { satc_accountingFinancial } from "Constants/TableColumns";
import RemoteTable from "Components/RemoteTable/RemoteTable";
import { useTranslation } from "react-i18next";
import { useSetState } from "Helpers/Hooks";
import MuiAutoComplete from "Components/MuiAutoComplete/MuiAutoComplete";
import MuiButton from "Components/MuiButton/MuiButton";
import Grid from "@material-ui/core/Grid";
import DatePickerComponent from "Components/DatePickerComponent/DatePickerComponent";
import {
  clearAccountingFinancial,
  getAccountingFinancial,
  getAccountingFinancialExport,
  getAccountsAllGroup,
} from "Stores/actions";
import { read } from "Helpers/LocalStorage";
import { useDispatch, useSelector } from "react-redux";
import BackDropLoading from "Components/BackDropLoading2/BackDropLoading";
import showNotify from "Helpers/Notify";
import PageTitle from "Components/PageTitle_IconCircle/PageTitle";
import * as Icons from "react-bootstrap-icons";
import moment from "moment-jalaali";
import { useTheme } from "@material-ui/core";
import { getLocaleDate } from "Helpers/GetLocaleDate";

export default function Transactions() {
  const { t } = useTranslation();
  const { direction } = useTheme();
  const dispatch = useDispatch();
  const [fromDate, setFromDate] = useState();
  const [toDate, setToDate] = useState();
  const [sizePerPage, setSizePerPage] = useState(10);
  const [loading, setLoading] = useState(true);
  const [change, setChange] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [tblData, setTblData] = useState([]);
  const [groupId, setGroupId] = useState(-1);
  const { accountingFinancial, allGroups, pagination } = useSelector(
    (state) => ({
      accountingFinancial: state.saAccounts.accountingFinancial,
      allGroups: state.saAccounts.allGroups,
      pagination: state.saAccounts.pagination,
    })
  );
  const [controls, setControls] = useSetState({
    groups: {
      type: "select",
      selectItems: [],
      value: "",
      validation: null,
    },
    fromDate: {
      type: "date",
      value: "",
      validation: null,
    },
    toDate: {
      type: "date",
      value: "",
      validation: null,
    },
  });

  useEffect(() => {
    onClear();
    onEnter();
  }, []);

  const onClear = async () => {
    await dispatch(clearAccountingFinancial());
    setGroupId(-1);
    setFromDate(moment().startOf('jMonth'));
    setToDate(moment());
    setTblData([]);
  };

  const onEnter = async () => {
    setLoading(true);
    await getAllGroups();
    setLoading(false);
  };

  const getAllGroups = async () => {
    return new Promise(async (resolve) => {
      const params = {
        KeyGroup: read("groupAKey"),
        KeyUser: read("userAKey"),
      };
      await dispatch(getAccountsAllGroup(params));
      resolve(true);
    });
  };

  useEffect(() => {
    if (allGroups) {
      const selectItems = allGroups.map((child) => {
        return {
          label: child.Name,
          value: child.Id,
        };
      });
      setControls({ groups: { selectItems } });
      setControls({ groups: { value: selectItems[0] } });
      setGroupId(selectItems[0].value);
    }
  }, [allGroups]);

  const autoCompleteHandler = async (e, value) => {
    const controlName = e.target.id.split("-")[0];
    setControls({ [controlName]: { value: value } });
    setGroupId(value.value);
    setChange(true);
  };

  const onTableChange = async (type, { page, sizePerPage }) => {
    if (type === "pagination") {
      await search(page, sizePerPage);
      setLoading(false);
    }
  };

  const handleFromDate = (value) => {
    setFromDate(value);
    setChange(true);
  };

  const handleToDate = (value) => {
    setToDate(value);
    setChange(true);
  };

  const handleSearch = async () => {
    await search();
    setLoading(false);
  };

  const search = async (_page = 1, _sizePerPage = sizePerPage) => {
    setLoading(true);
    if (groupId === -1) {
      showNotify({ text: t("chooseOneGroup"), customClass: "error" });
    } else if (!fromDate || !toDate) {
      showNotify({ text: t("checkDate"), customClass: "error" });
    } if(fromDate > toDate) {
      showNotify({ text: t("checkDate"), customClass: "error" });
    } else {
      setChange(false);
      const dataSearch = {
        GroupId: groupId,
        Date: `${getLocaleDate(fromDate, direction)}-${getLocaleDate(toDate, direction)}`,
      };
      return new Promise(async (resolve) => {
        const params = {
          KeyGroup: read("groupAKey"),
          KeyUser: read("userAKey"),
          Limit: _sizePerPage,
          Page: _page,
          ...dataSearch,
        };
        await dispatch(getAccountingFinancial(params));
        resolve(true);
      });
    }
  };

  useEffect(() => {
    if (accountingFinancial) {
      setTblData(accountingFinancial);
      setSizePerPage(pagination.Limit);
      setCurrentPage(pagination.Page);
      setTotal(pagination.Total);
    }
  }, [accountingFinancial]);

  const handleExcelExport = async () => {
    setLoading(true);
    if (groupId === -1) {
      showNotify({ text: t("chooseOneGroup"), customClass: "error" });
    } else if (!fromDate || !toDate) {
      showNotify({ text: t("checkDate"), customClass: "error" });
    } else {
      setChange(false);
      const dataSearch = {
        GroupId: groupId,
        Date: `${fromDate} - ${toDate}`,
      };
      const params = {
        KeyGroup: read("groupAKey"),
        KeyUser: read("userAKey"),
        ...dataSearch,
      };
      await dispatch(getAccountingFinancialExport(params, document));
    }
    setLoading(false);
  };

  return (
    <>
      <BackDropLoading loading={loading} />
      <div className="page-top-title-button my-3">
        <PageTitle title={<Translator id="transactions" />} />
        <div className="top-buttons">
          <MuiButton
            color="secondary"
            title={<Translator id="excely" />}
            innerIcon={<Icons.FiletypeXls />}
            onClick={handleExcelExport}
            disabled={loading || !(change && tblData.length !== 0)}
            hidden={!(change && tblData.length !== 0)}
            fullwidthOnSm
          />
        </div>
      </div>
      <MuiPaper>
        <Grid container spacing={2} className="mb-2">
          {Object.keys(controls).map((key) => {
            return (
              <Grid item md={3} sm={6} xs={12} key={key}>
                {controls[key].type === "select" && (
                  <MuiAutoComplete
                    autoSelect={true}
                    options={controls[key].selectItems}
                    label={<Translator id={key} />}
                    value={controls[key].value}
                    onChange={autoCompleteHandler}
                    id={key}
                  />
                )}
                {key === "fromDate" && (
                  <DatePickerComponent
                    label={t(key)}
                    value={fromDate}
                    onChange={handleFromDate}
                  />
                )}
                {key === "toDate" && (
                  <DatePickerComponent
                    label={t(key)}
                    value={toDate}
                    onChange={handleToDate}
                  />
                )}
              </Grid>
            );
          })}
          <Grid
            item
            md={3}
            sm={6}
            xs={12}
            className="margin-top-button no-sm-effect"
          >
            <MuiButton
              color="secondary"
              title={<Translator id="doFilter" />}
              innerIcon={<Icons.Funnel />}
              onClick={handleSearch}
              disabled={loading}
              fullwidthOnSm
            />
          </Grid>
        </Grid>
        <RemoteTable
          data={tblData}
          columns={satc_accountingFinancial}
          page={currentPage}
          sizePerPage={sizePerPage}
          totalSize={total}
          onTableChange={onTableChange}
        />
      </MuiPaper>
    </>
  );
}
